import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'
import { createClient, getDocByUid } from '@lib/prismicio'
import getGlobalStaticProps from '@lib/get-global-static-props'
import { SliceZone } from '@prismicio/react'
import { Breadcrumbs, Layout, Loader } from '@components/common'
import { components } from 'slices'
import { BreadcrumItem } from 'types/Common'
import { NextSeo } from 'next-seo'
import { PrismicDocument } from '@prismicio/types'
import commerce from '@lib/api/commerce'
import BrandCollection from '@components/collection/BrandCollection/BrandCollection'
import { pathToSearchState } from '@lib/algolia'
import { AnalyticsPageType } from '@shopify/hydrogen-react'

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
  previewData,
}: GetStaticPropsContext<{ slug: string }>) {
  const client = createClient({ previewData })

  const globalProps = await getGlobalStaticProps(client, locale)
  const config = { locale, locales }
  let page: PrismicDocument | { data: null } = { data: null }
  try {
    try {
      page = await client.getByUID('landing-page', params!.slug)
    } catch (e) {
      console.info(`No prismic document found for collection ${params!.slug}`)
    }

    const collectionPromise = commerce.getCollection({
      variables: { slug: params!.slug },
      config,
      preview,
    })

    const { collection } = await collectionPromise

    // for brand banner image
    const prismicBrandPageDocPromise = await getDocByUid(
      'brands-page',
      'brands-page',
      client,
      locale
    )

    const prismicBrandPageDoc: PrismicDocument | null =
      await prismicBrandPageDocPromise

    if (!page.data && !collection) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        ...globalProps,
        collection: JSON.parse(JSON.stringify(collection)) ? JSON.parse(JSON.stringify(collection)) : null,
        prismicBrandPageData: prismicBrandPageDoc?.data,
        page,
        analyticsPayload: {
          pageType: AnalyticsPageType.page,
        },
      },
      revalidate: 600,
    }
  } catch (error) {
    console.log('error', error)
    return {
      notFound: true,
    }
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const collections = [{ path: 'rings' }, { path: 'jewellery' }]
  return {
    paths: locales
      ? locales.reduce<string[]>((arr, locale) => {
          // Add a product path for every locale
          collections.forEach((collection: any) => {
            arr.push(`/${locale}/brands/${collection.path}`)
          })
          return arr
        }, [])
      : collections.map((collection: any) => `/brands/${collection.path}`),
    fallback: 'blocking',
  }
}

export default function Slug({
  page,
  collection,
  prismicBrandPageData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  const { asPath, query } = useRouter()
  const { data } = page || {}
  const { name } = data || {}
  const { slices } = data || {}

  const searchState = pathToSearchState(asPath)

  const collectionHandle = query!.slug as string

  if (router.isFallback) {
    return <Loader title="general-page-loader" />
  }

  const breadcrumbOptions: BreadcrumItem[] = [
    { link: '/brands', label: 'Brands' },
  ]

  return (
    <>
      <NextSeo
        canonical={`https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}${asPath}`}
      />
      <div className="wrapper">
        <Breadcrumbs current={name} options={breadcrumbOptions} />
      </div>

      {slices && <SliceZone slices={slices} components={components} />}
      {!slices && collection && (
        <BrandCollection
          collection={collection}
          prismicBrandPageData={prismicBrandPageData}
          collectionHandle={collectionHandle}
          searchState={searchState}
        />
      )}
    </>
  )
}

Slug.Layout = Layout
